import type { AssessmentInput, StockAssessment } from '@/types'

/**
 * Assessment Engine — Rules-based system that evaluates stocks on multiple
 * dimensions and produces recommendations for options trading strategies.
 */

function evaluateWheelQuality(input: AssessmentInput): string {
  const { ivRank = 0, avgVolume = 0 } = input
  const discount = ((input.sixMonthHigh - input.currentPrice) / input.sixMonthHigh) * 100
  const inDiscountZone = discount >= 15 && discount <= 30
  const highIV = ivRank > 30
  const highVolume = avgVolume > 1_000_000

  const score = [inDiscountZone, highIV, highVolume].filter(Boolean).length
  if (score >= 3) return 'High'
  if (score >= 2) return 'Medium'
  return 'Low'
}

function evaluateLeapCallQuality(input: AssessmentInput): string {
  const belowHigh = ((input.fiftyTwoWeekHigh - input.currentPrice) / input.fiftyTwoWeekHigh) * 100
  const deepDiscount = belowHigh > 20
  const lowEarningsRisk = !input.earningsDate || daysUntilEarnings(input.earningsDate) > 28

  if (deepDiscount && lowEarningsRisk) return 'High'
  if (deepDiscount || lowEarningsRisk) return 'Medium'
  return 'Low'
}

function evaluateLeapPutHedge(input: AssessmentInput): string {
  const highVix = input.vixLevel > 20
  const wideRange = ((input.fiftyTwoWeekHigh - input.fiftyTwoWeekLow) / input.fiftyTwoWeekLow) * 100 > 40
  const { ivRank = 0 } = input
  const goodPremium = ivRank > 25

  const score = [highVix, wideRange, goodPremium].filter(Boolean).length
  if (score >= 3) return 'High'
  if (score >= 2) return 'Medium'
  return 'Low'
}

function evaluateIVPremiumLevel(input: AssessmentInput): string {
  const { ivRank = 0 } = input
  if (ivRank > 50) return 'Rich'
  if (ivRank >= 25) return 'Fair'
  return 'Cheap'
}

function evaluateAssignmentComfort(input: AssessmentInput): string {
  const discount = ((input.sixMonthHigh - input.currentPrice) / input.sixMonthHigh) * 100
  if (discount >= 15 && discount <= 30) return 'Comfortable'
  if (discount >= 10) return 'Acceptable'
  return 'Uncomfortable'
}

function evaluateRiskLevel(input: AssessmentInput): string {
  const { ivRank = 0 } = input
  const earningsDays = input.earningsDate ? daysUntilEarnings(input.earningsDate) : 999
  const discount = ((input.sixMonthHigh - input.currentPrice) / input.sixMonthHigh) * 100

  let riskScore = 0
  if (earningsDays < 14) riskScore += 2
  else if (earningsDays < 28) riskScore += 1
  if (ivRank > 60) riskScore += 2
  else if (ivRank > 40) riskScore += 1
  if (discount > 30) riskScore += 2
  else if (discount > 20) riskScore += 1

  if (riskScore >= 4) return 'High'
  if (riskScore >= 2) return 'Medium'
  return 'Low'
}

function evaluateEarningsRisk(input: AssessmentInput): string {
  if (!input.earningsDate) return 'Low'
  const days = daysUntilEarnings(input.earningsDate)
  if (days <= 14) return 'High'
  if (days <= 28) return 'Medium'
  return 'Low'
}

function evaluateThreeYearOwnScore(input: AssessmentInput): string {
  // Based on sector strength and market cap (simplified)
  const { sector = '', marketCap = 0 } = input
  const techSectors = ['Technology', 'Communication Services']
  const isTech = techSectors.includes(sector)
  const isLargeCap = marketCap > 100_000_000_000

  if (isTech && isLargeCap) return 'Strong'
  if (isTech || isLargeCap) return 'Moderate'
  return 'Weak'
}

function evaluateAIExposureStrength(input: AssessmentInput): string {
  // Based on AI Stack Role classification
  const { sector = '' } = input
  if (sector === 'Technology') return 'Core'
  if (sector === 'Communication Services') return 'Adjacent'
  return 'Peripheral'
}

function evaluateBestStrategy(input: AssessmentInput): string {
  const discount = ((input.sixMonthHigh - input.currentPrice) / input.sixMonthHigh) * 100
  const { ivRank = 0 } = input

  if (discount >= 15 && discount <= 30 && ivRank > 30) return 'CSP'
  if (discount < 10 && ivRank > 30) return 'CC'
  if (discount > 20) return 'LEAP Call'
  if (ivRank > 50) return 'CSP'
  return 'Watch'
}

function evaluatePriorityTier(input: AssessmentInput): string {
  const discount = ((input.sixMonthHigh - input.currentPrice) / input.sixMonthHigh) * 100
  const { ivRank = 0 } = input
  const earningsDays = input.earningsDate ? daysUntilEarnings(input.earningsDate) : 999

  const inZone = discount >= 15 && discount <= 30
  const goodIV = ivRank > 30
  const safeFromEarnings = earningsDays > 14

  if (inZone && goodIV && safeFromEarnings) return 'Tier 1'
  if ((inZone && safeFromEarnings) || (goodIV && safeFromEarnings)) return 'Tier 2'
  return 'Tier 3'
}

function daysUntilEarnings(earningsDate: string): number {
  const now = new Date()
  const earnings = new Date(earningsDate)
  const diffMs = earnings.getTime() - now.getTime()
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24))
}

/**
 * Run the full assessment engine on a stock input.
 * Returns a complete StockAssessment with all 14 fields populated.
 */
export function runAssessment(stockId: string, input: AssessmentInput): StockAssessment {
  return {
    stockId,
    subCategory: input.sector || 'General',
    aiStackRole: evaluateAIExposureStrength(input),
    priorityTier: evaluatePriorityTier(input),
    bestStrategy: evaluateBestStrategy(input),
    wheelQuality: evaluateWheelQuality(input),
    leapCallQuality: evaluateLeapCallQuality(input),
    leapPutHedgeSuitability: evaluateLeapPutHedge(input),
    ivPremiumLevel: evaluateIVPremiumLevel(input),
    assignmentComfort: evaluateAssignmentComfort(input),
    riskLevel: evaluateRiskLevel(input),
    earningsRisk: evaluateEarningsRisk(input),
    threeYearOwnScore: evaluateThreeYearOwnScore(input),
    aiExposureStrength: evaluateAIExposureStrength(input),
    // CSV-sourced fields — populated via import, not computed
    watchlistStatus: '',
    investmentThesis: '',
    assignmentQuality: '',
    ownershipDesire: '',
    technicalStatus: '',
    idealEntryZone: '',
    leapCatalyst: '',
    gainPotential: '',
    recommendationStatus: '',
    reviewPriority: '',
    portfolioHedgeCandidate: '',
    notes: '',
    computedAt: new Date(),
  }
}

/**
 * Get all assessment field names (for iteration/display).
 */
export const ASSESSMENT_FIELDS = [
  'subCategory',
  'aiStackRole',
  'priorityTier',
  'bestStrategy',
  'wheelQuality',
  'leapCallQuality',
  'leapPutHedgeSuitability',
  'ivPremiumLevel',
  'assignmentComfort',
  'riskLevel',
  'earningsRisk',
  'threeYearOwnScore',
  'aiExposureStrength',
  'watchlistStatus',
  'investmentThesis',
  'assignmentQuality',
  'ownershipDesire',
  'technicalStatus',
  'idealEntryZone',
  'leapCatalyst',
  'gainPotential',
  'recommendationStatus',
  'reviewPriority',
  'portfolioHedgeCandidate',
  'notes',
] as const

export const ASSESSMENT_LABELS: Record<string, string> = {
  subCategory: 'Sub-Category',
  aiStackRole: 'AI Stack Role',
  priorityTier: 'Priority Tier',
  bestStrategy: 'Best Strategy',
  wheelQuality: 'Wheel Quality',
  leapCallQuality: 'LEAP Call Quality',
  leapPutHedgeSuitability: 'LEAP Put/Hedge',
  ivPremiumLevel: 'IV/Premium Level',
  assignmentComfort: 'Assignment Comfort',
  riskLevel: 'Risk Level',
  earningsRisk: 'Earnings Risk',
  threeYearOwnScore: '3-Year Own Score',
  aiExposureStrength: 'AI Exposure',
  watchlistStatus: 'Watchlist Status',
  investmentThesis: 'Investment Thesis',
  assignmentQuality: 'Assignment Quality',
  ownershipDesire: 'Ownership Desire',
  technicalStatus: 'Technical Status',
  idealEntryZone: 'Ideal Entry Zone',
  leapCatalyst: 'LEAP Catalyst',
  gainPotential: '30-60% Gain Potential',
  recommendationStatus: 'Recommendation Status',
  reviewPriority: 'Review Priority',
  portfolioHedgeCandidate: 'Portfolio Hedge',
  notes: 'Notes',
}
